/**
 * Per-diem billing window vocabulary and month math.
 *
 * A per-diem program qualifies a patient inside a repeating window. The window's
 * length and anchor are both encoded in the single `perDiem.billingWindowStartDay`
 * string: the weekday values (and 'Program admission day') describe a 7-day week,
 * while the two monthly values describe a one-month window. Any value that is not
 * one of the monthly values — including an absent or unrecognized one — is weekly,
 * so every program configured before monthly existed keeps its 7-day window.
 *
 * The settings screen and the per-diem report both read this module so the stored
 * strings live in exactly one place.
 *
 * These helpers are pure (no Vue, no API) so they can be unit-tested and reused
 * outside the component layer.
 */

import { addMonths, differenceInMonths, subDays } from 'date-fns';

export const PROGRAM_ADMISSION_DAY = 'Program admission day';
export const MONTHLY_FROM_ADMISSION_DATE = 'Monthly from admission date';
export const FIRST_OF_MONTH = 'First of month';

export const WEEKLY_START_DAY_OPTIONS = [PROGRAM_ADMISSION_DAY, 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const MONTHLY_START_DAY_OPTIONS = [MONTHLY_FROM_ADMISSION_DATE, FIRST_OF_MONTH];

const MAX_WEEKLY_SERVICE_DAYS = 7;
// February's window is only 28 days long, so a higher cap would let an account save a
// threshold that can never be met.
const MAX_MONTHLY_SERVICE_DAYS = 28;

/**
 * Whether a program's per-diem rules use a one-month window rather than a 7-day week.
 *
 * @param {object} perDiem the program's `perDiem` rules
 * @returns {boolean} true only for the two monthly start-day values
 */
export function isMonthlyWindow(perDiem) {
    return MONTHLY_START_DAY_OPTIONS.includes(perDiem?.billingWindowStartDay);
}

/**
 * Whether a program bills once per window rather than once per qualifying day.
 *
 * A stored minimum of exactly 0 service days means the charge is for enrollment itself:
 * the window bills a single flat unit whether or not any service was delivered. An absent
 * minimum is not 0 — those programs keep the per-day model.
 *
 * @param {object} perDiem the program's `perDiem` rules
 * @returns {boolean} true only when `requiredMinimumServiceDays` is the number 0
 */
export function billsPerWindow(perDiem) {
    return perDiem?.requiredMinimumServiceDays === 0;
}

/**
 * The start-day choices offered for a window length.
 *
 * @param {boolean} isMonthly true for the monthly anchors, false for the weekly ones
 * @returns {string[]} the storable `billingWindowStartDay` values
 */
export function perDiemStartDayOptions(isMonthly) {
    return isMonthly ? MONTHLY_START_DAY_OPTIONS : WEEKLY_START_DAY_OPTIONS;
}

/**
 * The highest number of qualifying service days a window can require.
 *
 * @param {object} perDiem the program's `perDiem` rules
 * @returns {number} 28 for a monthly window, 7 for a weekly one
 */
export function maxRequiredServiceDays(perDiem) {
    return isMonthlyWindow(perDiem) ? MAX_MONTHLY_SERVICE_DAYS : MAX_WEEKLY_SERVICE_DAYS;
}

/**
 * The monthly window anchored on the admission day of month that contains `referenceDate`.
 *
 * Windows tile from the admission date: each one starts on the admission day of month and
 * ends the day before the next one starts, so consecutive windows are contiguous and share
 * no day. Every window is measured from the original admission date rather than chained off
 * the previous window, which is what lets a 31st anchor survive a short month and come back
 * on the 31st afterwards instead of sticking on the 28th.
 *
 * @param {Date} admission facility-local admission day
 * @param {Date} referenceDate facility-local day the window must contain
 * @returns {{startDate: Date, endDate: Date}} inclusive window bounds
 */
export function monthlyWindowBounds(admission, referenceDate) {
    let n = Math.max(0, differenceInMonths(referenceDate, admission));

    const at = (k) => ({ startDate: addMonths(admission, k), endDate: subDays(addMonths(admission, k + 1), 1) });

    let w = at(n);

    // A month count truncated by a short month can land a window entirely before the
    // reference date (admission Jan 31 is two months, not three, before Apr 30), so walk
    // forward until the window actually reaches it.
    while (w.endDate < referenceDate) w = at(++n);

    // A future-dated admission counts as a negative number of months; clamp back to the
    // first window so the result is never a window that precedes admission.
    while (n > 0 && w.startDate > referenceDate) w = at(--n);

    return w;
}
